import { useEffect, useState } from 'react';

type Corner = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

const CORNERS: Corner[] = ['top-left', 'top-right', 'bottom-left', 'bottom-right'];
const ROTATE_INTERVAL_MS = 30_000;

const CORNER_CLASS: Record<Corner, string> = {
  'top-left': 'top-4 left-4',
  'top-right': 'top-4 right-4',
  'bottom-left': 'bottom-4 left-4',
  'bottom-right': 'bottom-4 right-4',
};

type Props = {
  text: string;
};

/**
 * Watermark overlay rendered on top of the protected video iframe. Rotates
 * between four corners every 30 seconds so a casual screen-recording crop has
 * to give up significant screen real estate to remove it. `pointer-events-none`
 * keeps player controls (play/pause/fullscreen) clickable.
 *
 * Forensic intent only — see docs/architecture/04-video-protection.md.
 */
export function Watermark({ text }: Props) {
  const [corner, setCorner] = useState<Corner>('top-left');

  useEffect(() => {
    const id = setInterval(() => {
      setCorner(CORNERS[Math.floor(Math.random() * CORNERS.length)] ?? 'top-left');
    }, ROTATE_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  return (
    <div
      className={`absolute ${CORNER_CLASS[corner]} pointer-events-none transition-all duration-700 ease-out`}
    >
      <span
        className="inline-flex items-center rounded-pill bg-black/25 backdrop-blur-sm px-2.5 py-1 font-mono text-[10px] tracking-wide text-white/75"
        style={{ textShadow: '0 0 2px rgba(0,0,0,0.9)' }}
      >
        {text}
      </span>
    </div>
  );
}
